import React, { CSSProperties, useCallback, useEffect, useState } from 'react';
import { GridColDef, GridPanel } from '../../layout/gridpanel';
import { Button } from '../button';
import { formatInteger, getDecimalSeparator, parseInteger } from '@unifocus/js-utils';
import { Icon } from '@unifocus/react-icons';
import { faBackspace } from '@fortawesome/pro-regular-svg-icons';
import { HorizontalSpacer } from '../../layout/horizontalspacer';

/**
 * {@docCategory Keypad}
 */
export interface KeypadProps {

   /**
    * The keypad entry mode
    * @defaultvalue 'decimal'
    */
   mode?: 'integer' | 'decimal' | 'digits';

   /**
    * The maximum number of digits for all modes, not including the decimal character
    * @defaultvalue 10
    */
   maxDigits?: number;

   /**
    * The maximum number of decimal places for decimal mode
    * @defaultvalue 2
    */
   maxDecimalPlaces?: number;

   /**
    * The initial value for the internal number state of the keypad as a string.  This string
    * must be parsable by the component based on what mode is selected (i.e. it should not contain
    * alphabetic characters, etc.)
    */
   initialValue?: string;

   /**
    * The class name of the keypad
    */
   className?: string;

   /**
    * Style of the keypad
    */
   style?: CSSProperties;

   /**
    * The callback event that happens every time the user presses a button.  The callback
    * passes the current full internal value of the keypad as a string.
    */
   onChange?: (value: string | null) => void;
}

export const Keypad = (props: KeypadProps) => {

   const { initialValue = null, mode = 'decimal', maxDigits: propMaxDigits, maxDecimalPlaces: propMaxDecimalPlaces, onChange } = props;

   const maxDecimalPlaces = (mode === 'decimal' && propMaxDecimalPlaces && propMaxDecimalPlaces > 0) ? propMaxDecimalPlaces : 2;
   const maxDigits = (propMaxDigits !== undefined && propMaxDigits > 0) ? propMaxDigits : 10;

   const [value, setValue] = useState<string | null>(getValidatedInitialValue(mode, initialValue, maxDigits, maxDecimalPlaces));

   useEffect(()=>{
      setValue(() => {
         const newValue = getValidatedInitialValue(mode, initialValue, maxDigits, maxDecimalPlaces);
         if (newValue !== initialValue && onChange) {
            onChange(newValue);
         }
         return newValue;
      });
   }, [mode, initialValue, maxDigits, maxDecimalPlaces, onChange]);

   const buttonOnClickHandler = useCallback((buttonType: ButtonType)=>{
      return () => {
         const newValue = getNewValue(value, buttonType, mode, maxDigits, maxDecimalPlaces);
         setValue(newValue);
         if (onChange) {
            onChange(newValue);
         }
      };
   },[onChange, value, mode, maxDigits, maxDecimalPlaces]);

   return (
      <GridPanel
         columns={gridLayoutColDef}
         rows={4}
         horizontalFill={true}
         verticalFill={false}
         className={`uf-keypad ${props.className || ''}`}
         style={props.style}
         gap={true}
         padding={true}
      >
         {getButton(buttonOnClickHandler(ButtonType.ONE),1)}
         {getButton(buttonOnClickHandler(ButtonType.TWO),2)}
         {getButton(buttonOnClickHandler(ButtonType.THREE),3)}
         {getButton(buttonOnClickHandler(ButtonType.FOUR),4)}
         {getButton(buttonOnClickHandler(ButtonType.FIVE),5)}
         {getButton(buttonOnClickHandler(ButtonType.SIX),6)}
         {getButton(buttonOnClickHandler(ButtonType.SEVEN),7)}
         {getButton(buttonOnClickHandler(ButtonType.EIGHT),8)}
         {getButton(buttonOnClickHandler(ButtonType.NINE),9)}
         {mode==='decimal' ?
          getButton(buttonOnClickHandler(ButtonType.DECIMAL),getDecimalSeparator())
          :  <HorizontalSpacer/>
         }
         {getButton(buttonOnClickHandler(ButtonType.ZERO),0)}
         {getButton(buttonOnClickHandler(ButtonType.BACKSPACE), undefined, BackspaceIcon)}
      </GridPanel>
   );
};

const getButton = (onClickHandler: ()=>void, value: number | string | undefined, icon?: React.ReactNode) => {
   return (<Button block={true} type={'text'} onClick={onClickHandler} className={'uf-keypad-button'} icon={icon}>
      {typeof value === 'string' ? value : (value !== undefined ? formatInteger(value) : undefined)}
   </Button>);
};

enum ButtonType {
   ZERO,
   ONE,
   TWO,
   THREE,
   FOUR,
   FIVE,
   SIX,
   SEVEN,
   EIGHT,
   NINE,
   DECIMAL,
   BACKSPACE
}

const gridLayoutColDef = [
   {
      justify: 'center',
      align: 'center'
   },
   {
      justify: 'center',
      align: 'center'
   },
   {
      justify: 'center',
      align: 'center'
   }
] as GridColDef[];

const BackspaceIcon = <Icon icon={faBackspace}/>;

const getValidatedInitialValue = (mode: 'integer' | 'decimal' | 'digits', value: string | null, maxDigits: number, maxDecimalPlaces: number): string | null => {

   if (value === '') {
      value = null;
   }

   if (value === null) {
      return null;
   }

   if (mode === 'integer') {
      const parsedValue = '' + parseInteger(value);
      if (value !== parsedValue || parsedValue.length > maxDigits) {
         console.error("Invalid keypad initial value '"+value+"', parsedValue = '"+parsedValue+"'");
         value = null;
      }
   }
   else if (mode === 'decimal') {
      const decimalSeparator = getDecimalSeparator();

      const decimalIndex = value.indexOf(decimalSeparator);
      if (decimalIndex >= 0) {
         const parsedWholeValue = '' + parseInteger(value.substring(0, decimalIndex));
         const decimalPartOfValue = value.substring(decimalIndex+1);
         const parsedDecimalValue = getZeroPaddedInteger(decimalPartOfValue);

         const parsedValue = parsedWholeValue + decimalSeparator + parsedDecimalValue;
         if (value !== parsedValue || parsedValue.length > (maxDigits+1) || parsedDecimalValue.length > maxDecimalPlaces) {
            console.error("Invalid keypad initial value '"+value+"', parsedValue = '"+parsedValue+"'");
            value = null;
         }
      }
      else {
         const parsedValue = '' + parseInteger(value);
         if (value !== parsedValue || parsedValue.length > maxDigits) {
            console.error("Invalid keypad initial value '"+value+"', parsedValue = '"+parsedValue+"'");
            value = null;
         }
      }
   }
   else if (mode === 'digits') {
      const parsedValue = getZeroPaddedInteger(value);
      if (value !== parsedValue || parsedValue.length > maxDigits) {
         console.error("Invalid keypad initial value '"+value+"', parsedValue = '"+parsedValue+"'");
         value = null;
      }
   }

   return value;
};

const getZeros = (numberOfZeros: number): string => {

   let value = '';
   for (let index = 0; index < numberOfZeros; index++) {
      value+='0';
   }
   return value;
};

const getZeroPaddedInteger = (value: string): string => {
   const parsedValue = (getZeros(value.length) + parseInteger(value));
   return parsedValue.substring(parsedValue.length - value.length);
};

const getNewValue = (oldValue: string | null, buttonType: ButtonType, mode: 'integer' | 'decimal' | 'digits', maxDigits: number, maxDecimalPlaces: number) => {

   let value = oldValue;

   if (buttonType === ButtonType.BACKSPACE) {

      if (oldValue !== null && oldValue.length > 0) {
         const len = oldValue.length;
         value = oldValue.substr(0, len-1);
         if (value.length === 0) {
            value = null;
         }
         else if (mode !== 'digits' && value.length === 1 && value === formatInteger(0)) {
            value = null;
         }
      }
   }
   else {
      const decimalSeparator = getDecimalSeparator();

      if (oldValue === null) {
         if (buttonType === ButtonType.DECIMAL) {
            value = formatInteger(0) + decimalSeparator;
         }
         else {
            if (mode === 'digits' || buttonType !== 0)
            {
               value = "" + buttonType;
            }
         }
      }
      else {
         const oldValueIncludesDecimalSeparator = oldValue.includes(decimalSeparator);
         const realMaxDigits = oldValueIncludesDecimalSeparator ? oldValue.length - 1 : oldValue.length;
         if (realMaxDigits < maxDigits) {
            if (buttonType === ButtonType.DECIMAL) {
               if (!oldValueIncludesDecimalSeparator) {
                  value = oldValue + decimalSeparator;
               }
            }
            else {
               if (oldValueIncludesDecimalSeparator) {
                  const curNumDecimalPlaces = oldValue.length - (oldValue.indexOf(decimalSeparator) + 1);
                  if (curNumDecimalPlaces < maxDecimalPlaces) {
                     value = oldValue + buttonType;
                  }
               }
               else {
                  value = oldValue + buttonType;
               }
            }
         }
      }
   }

   return value;
};
